import { gridFocusCellSelector } from '@mui/x-data-grid-premium';

/**
 * DataGridPremium 셀 복사·붙여넣기 보조 (여러 줄 Note 처럼 셀 안에 줄바꿈/탭이 있는 값용).
 * 셀 안의 줄바꿈/탭을 CSV 식 큰따옴표로 감싸 복사하고, 붙여넣을 때 되돌린다.
 * 사용처: Shipment Rate Info(`features/shipments/components/Rate/UpdateRateInfoTable.js`),
 * Rate Management 상세(`features/pricing/components/RateManagementDetailPanel.js`).
 */

/** 셀 시작 위치에 놓인 CSV 큰따옴표 셀 (`"..."`, `""` 는 리터럴 큰따옴표). */
const QUOTED_CELL = /(^|[\t\r\n])"((?:[^"]|"")*)"(?=[\t\r\n]|$)/g;
const PLACEHOLDER = /\0(\d+)\0/g;

/**
 * 클립보드 텍스트 → 그리드 셀: 탭 = 열, 줄바꿈 = 행.
 * 큰따옴표로 감싼 셀은 탭/줄바꿈(여러 줄 Note)을 포함할 수 있으므로, 먼저 자리표시자로 들어낸 뒤
 * 분할하고 다시 되돌린다. Excel 과 아래 복사 핸들러 모두 이 형식을 만든다.
 * 끝의 줄바꿈(Windows Excel)은 빈 행이 하나 더 붙지 않도록 버린다.
 */
export const splitClipboardPastedText = (text) => {
    const lifted = [];
    const flat = text.replace(QUOTED_CELL, (_, delimiter, inner) => {
        lifted.push(inner.replace(/""/g, '"').replace(/\r\n?/g, '\n'));
        return `${delimiter}\0${lifted.length - 1}\0`;
    });
    const restore = (cell) => cell.replace(PLACEHOLDER, (_, index) => lifted[index]);

    return flat
        .replace(/\r?\n$/, '')
        .split(/\r\n|\n|\r/)
        .map((row) => row.split('\t').map(restore));
};

/** 여러 줄 Note 가 위 분할을 통과하도록 CSV 식으로 감싼다. */
export const quoteClipboardCell = (value) => {
    const str = value == null ? '' : String(value);
    return /[\t\n\r"]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

/**
 * `DataGridPremium` 의 `onClipboardCopy` 용 핸들러.
 * 그리드 기본 복사는 셀을 감싸지 않아 Note 안의 줄바꿈이 행 구분과 구별되지 않는다.
 * 선택된 셀을 CSV 식 큰따옴표로 다시 직렬화해 클립보드를 덮어쓴다(감쌀 필요가 있을 때만).
 */
export const makeQuotedClipboardCopyHandler = (apiRef) => () => {
    const api = apiRef.current;
    if (!api || !navigator.clipboard) return;
    const cellValue = (id, field) => quoteClipboardCell(api.getCellParams(id, field).formattedValue);
    const selectedCells = api.getSelectedCellsAsArray?.() ?? [];
    let text;
    if (selectedCells.length > 1) {
        const rowsById = new Map();
        selectedCells.forEach(({ id, field }) => {
            if (!rowsById.has(id)) rowsById.set(id, []);
            rowsById.get(id).push(cellValue(id, field));
        });
        text = Array.from(rowsById.values())
            .map((cells) => cells.join('\t'))
            .join('\r\n');
    } else {
        const focused = gridFocusCellSelector(apiRef) ?? selectedCells[0];
        if (!focused) return;
        text = cellValue(focused.id, focused.field);
    }
    if (!/"/.test(text)) return; // 감쌀 것이 없으면 그리드 기본 복사 결과 그대로 둔다
    navigator.clipboard.writeText(text).catch(() => {});
};
